import React, { useState, useEffect, useRef } from 'react';
import {
	forbiddenChars,
	SCANNED_VALUE_DATA_TYPES,
	ZPL_TEMPLATE_FOR_BARCODE,
} from '../../constants';
import {
	Box,
	CircularProgress,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	Paper,
	TextField,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import styles from './ScanOrEnterBarcodeForm.module.scss';
import { useAlphanumericInput } from '../../hooks/useAlphanumericInput';
import { formatZplTemplate } from '../../utils/utils';

const classNames = {
	textInput: {
		border: '1px solid black',
		borderRadius: 2,
		background: ({ palette }) =>
			palette.mode === 'dark' ? '#1E1E1E' : palette.background.neutral,
		paddingLeft: 2,

		'& .MuiInputBase-input': {
			color: ({ palette }) => (palette.mode === 'dark' ? '#0F107B' : '#0F107B'),
			fontWeight: 'bold',
			borderRadiusRightTop: '18px',
			'@media (min-width:3840px)': {
				fontSize: '28px', // scale for 4K
				padding: '18px 24px', // adjust box height
			},
		},
		'& .MuiInputBase-input::placeholder': {
			color: ({ palette }) => (palette.mode === 'dark' ? 'rgba(15, 16, 123, 0.6)' : 'rgba(15, 16, 123, 0.6)'),
			opacity: 1,
			'@media (min-width:3840px)': {
				fontSize: '28px', // scale placeholder for 4K
			},
		},
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				border: '1px solid black',
				borderTopRightRadius: '20px',
				borderBottomRightRadius: '20px',
				'@media (min-width:3840px)': {
					borderRadius: '28px',
				},
			},
		},
	},
	forwardBtn: {
		marginRight: 0,
		marginTop: -2,
		marginBottom: -2,

		px: 5,

		background: 'linear-gradient(180deg, #0F107B 0%, #1B1EE1 100%)',
		border: '2px solid #0F107B',
		borderRadius: '18px',
		color: ({ palette }) => (palette.mode === 'dark' ? '#FFFFFF' : 'white'),

		'&:hover': {
			background: 'linear-gradient(180deg, #1B1EE1 0%, #2D2FFF 100%)',
		},

		'&.Mui-disabled': {
			color: 'rgba(255, 255, 255, 0.5)',
			background: 'linear-gradient(180deg, #6b6b6b 0%, #444 100%)',
			border: '2px solid #666',
			filter: 'none',
		},
		'@media (min-width:3840px)': {
			transform: 'scale(1.6)',
			borderRadius: '28px',
			padding: '8px',
			'& svg': { fontSize: 72 }, // bigger arrow
		},
	},
};

const LOCAL_STORAGE_KEY = 'barcode_history';
const ScanOrEnterBarcodeForm = ({
	inputRef = null,
	onSubmit,
	themeMode,
	inputProps = {},
	isAlphaNumericValidation = true,
	enableAutoEnter = false,
	maxLength = null,
	type = 'ALPHA_NUMERIC',
	textInputSx,
	forwardBtnSx,
	nameOfComponent,
	disabled = false,
	onValueChange,
	resetTrigger = 0,
	placeholder = 'SCAN VIN',
	filterButtonColumnFlag,
	// new props for ZPL printing on scan
	printZplOnScan = false,
	zplTemplate = ZPL_TEMPLATE_FOR_BARCODE,
	...otherProps
}) => {
	const {
		value: barcodeValue,
		setValue,
		handleChange,
		handlePaste,
		reset,
	} = useAlphanumericInput();

	const [barcodeHistory, setBarcodeHistory] = useState([]);
	const [showHistory, setShowHistory] = useState(false);
	const isSelectingHistory = useRef(false);
	const containerRef = useRef(null);
	const internalInputRef = useRef(null);
	const inputRefToUse = inputRef || internalInputRef;
	const focusTimeoutRef = useRef(null);
	const [isFocused, setIsFocused] = useState(false);
	const [isDisabled, setisDisabled] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		const ae = document.activeElement;
		const stored = localStorage.getItem(LOCAL_STORAGE_KEY);

		if (!isEditableElement(ae) || containerRef.current?.contains(ae)) {
			inputRefToUse.current?.focus({ preventScroll: true });
		}

		if (stored) {
			try {
				setBarcodeHistory(JSON.parse(stored));
			} catch (err) {
				console.error('Error parsing stored barcode history:', err);
			}
		}
	}, []);

	useEffect(() => {
		if (resetTrigger > 0) {
			reset('');
			if (onValueChange) {
				onValueChange('');
			}
		}
	}, [resetTrigger, onValueChange]);

	useEffect(() => {
		const interval = setInterval(() => {
			const ae = document.activeElement;

			const anotherEditableActive =
				isEditableElement(ae) && !containerRef.current?.contains(ae);
			if (
				!isFocused &&
				inputRefToUse.current &&
				document.activeElement !== inputRefToUse.current &&
				!anotherEditableActive
			) {
				inputRefToUse.current.focus({ preventScroll: true });
			}
		}, 5000);

		return () => clearInterval(interval);
	}, [isFocused]);

	useEffect(() => {
		if (maxLength) {
			if (barcodeValue.length <= maxLength) {
				if (onValueChange) {
					onValueChange(barcodeValue);
				}
			}
		} else {
			if (onValueChange) {
				onValueChange(barcodeValue);
			}
		}
	}, [barcodeValue]);

	const isEditableElement = (el) => {
		if (!el) return false;
		if (el.isContentEditable) return true;

		const tag = el.tagName?.toLowerCase();
		if (['input', 'textarea', 'select'].includes(tag)) return true;

		const role = el.getAttribute?.('role');
		if (role === 'textbox' || role === 'combobox') return true;

		return false;
	};

	const resetBarcodeValue = () => {
		reset('');
		if (onValueChange) {
			onValueChange('');
		}
	};

	const saveToHistory = (value) => {
		const newHistory = [
			value,
			...barcodeHistory.filter((v) => v !== value),
		].slice(0, 10);
		setBarcodeHistory(newHistory);
		localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newHistory));
	};

	const generateAndPrintZpl = (materialNumber) => {
		if (!materialNumber) return;
		try {
			const zpl = formatZplTemplate(zplTemplate, materialNumber);
			const zplstring = encodeURIComponent(zpl);
			window.location.href = `nirmaan://?cmd=print&zplstring=${zplstring}`;
		} catch (err) {
			console.error('Error generating/printing ZPL', err);
		}
	};

	const handleFocus = () => {
		setIsFocused(true);

		if (focusTimeoutRef.current) {
			clearTimeout(focusTimeoutRef.current);
		}

		if (barcodeHistory.length > 0) setShowHistory(true);
	};

	const handleBlur = () => {
		setIsFocused(false);

		if (!isSelectingHistory.current) {
			setTimeout(() => {
				const ae = document.activeElement;
				const anotherEditableActive =
					isEditableElement(ae) && !containerRef.current?.contains(ae);

				if (
					inputRefToUse.current &&
					!isSelectingHistory.current &&
					!anotherEditableActive
				) {
					inputRefToUse.current.focus({ preventScroll: true });
				}
			}, 5000);
		}
	};

	const handleSelectHistory = (value) => {
		isSelectingHistory.current = true;
		setValue(value);
		if (onValueChange) {
			onValueChange(value);
		}
		setShowHistory(false);
		setTimeout(() => {
			isSelectingHistory.current = false;
		}, 200);
	};

	const handleSubmit = async (event) => {
		event?.preventDefault();

		if (disabled || isLoading || !barcodeValue) return;
		if (typeof onSubmit !== 'function') {
			console.error(
				'ScanOrEnterBarcodeForm requires `onSubmit` to be a function.'
			);
			return;
		}

		try {
			setIsLoading(true);

			const barcodeValueUpper = barcodeValue.toUpperCase()
			await onSubmit(barcodeValueUpper, resetBarcodeValue);

			saveToHistory(barcodeValueUpper);
			setShowHistory(false);

			if (printZplOnScan && barcodeValueUpper) {
				const zpl = formatZplTemplate(zplTemplate, barcodeValueUpper);
				const zplstring = encodeURIComponent(zpl);
				window.location.href = `nirmaan://?cmd=print&zplstring=${zplstring}`;
			}
		} catch (error) {
			console.error('Submit failed', error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<form
			className={styles.formContainer}
			onSubmit={handleSubmit}
			ref={containerRef}
		>
			<Box
				sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
			>
				<TextField
					inputRef={inputRefToUse}
					fullWidth
					type="text"
					disabled={filterButtonColumnFlag || isDisabled || isLoading}
					variant="standard"
					value={barcodeValue}
					placeholder={placeholder}
					onChange={(e) => {
						if (isAlphaNumericValidation) {
							handleChange(e, type, 'uppercase');
						} else {
							setValue(e.target.value.toUpperCase());
						}
					}}

					onPaste={(e) => {
						e.preventDefault();
						if (isAlphaNumericValidation) {
							handlePaste(e, type, 'uppercase');
						} else {
							const pasted = e.clipboardData.getData('text');
							setValue(pasted.toUpperCase());
						}
					}}
					onFocus={handleFocus}
					onBlur={handleBlur}
					sx={{
						...classNames.textInput,
						background: (theme) =>
							theme.palette.mode === 'dark' ? '#FBFBFB' : 'transparent',
						borderWidth: '1px',
						borderTopRightRadius: '18px',
						borderBottomRightRadius: '18px',
						borderTopLeftRadius: '8px',
						borderBottomLeftRadius: '8px',

						'&:hover': {
							background: (theme) =>
								theme.palette.mode === 'dark' ? '#FFFFFF' : '#FFFAFA',
							borderColor: (theme) =>
								theme.palette.mode === 'dark' ? '#666' : '#D9D9D9',
						},
						'&.Mui-focused': {
							borderColor: '#3f51b5',
							boxShadow: (theme) =>
								theme.palette.mode === 'dark'
									? '0 0 6px 2px rgba(63, 81, 181, 0.7)'
									: '0 0 6px 2px rgba(63, 81, 181, 0.4)',
							background: (theme) =>
								theme.palette.mode === 'dark' ? '#FFFFFF' : '#FFFFFF',
						},
						// 4K scaling using px
						'@media (min-width:3840px)': {
							'& .MuiInputBase-input': {
								fontSize: '36px',
								padding: '18px 24px',
							},
							'& .MuiOutlinedInput-root': {
								borderRadius: '20px',
							},
						},
						...textInputSx,
					}}
					inputProps={{
						'data-testid': 'enter-barcode-input',
						...inputProps,
					}}
					InputProps={{
						disableUnderline: true,
						endAdornment: (
							<IconButton
								data-testid="next-button"
								edge="end"
								disabled={
									!barcodeValue?.length ||
									isLoading ||
									typeof onSubmit !== 'function'
								}
								onClick={handleSubmit}
								sx={{ ...classNames.forwardBtn }}
							>
								{isLoading ? (
									<CircularProgress size={24} sx={{ color: 'white' }} />
								) : (
									<ArrowForwardIosIcon
										sx={{
											fontSize: {
												xs: 39,
												md: 42,
												lg: 51,
											},
										}}
									/>
								)}
							</IconButton>
						),
					}}
					{...otherProps}
				/>

				{nameOfComponent === 'ReceivePart' &&
					showHistory &&
					barcodeHistory.length > 0 && (
						<Paper
							sx={{
								position: 'absolute',
								top: '100%',
								left: 0,
								right: 0,
								zIndex: 10,
								maxHeight: 200,
								overflowY: 'auto',
								mt: 1,
								'@media (min-width:3840px)': {
									maxHeight: 400,
									'& .MuiListItemButton-root': {
										fontSize: '26px',
										padding: '20px 24px',
									},
								},
							}}
						>
							<List dense>
								{barcodeHistory.map((entry, index) => (
									<ListItem disablePadding key={index}>
										<ListItemButton
											onMouseDown={() => (isSelectingHistory.current = true)}
											onClick={() => handleSelectHistory(entry)}
										>
											{entry}
										</ListItemButton>
									</ListItem>
								))}
							</List>
						</Paper>
					)}
			</Box>
		</form>
	);
};

export default ScanOrEnterBarcodeForm;
